import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProductForm from '../../components/Admin/ProductForm';
import ProductTable from '../../components/Admin/ProductTable';
import ProductFilters from '../../components/Admin/ProductFilters';
import ProductPagination from '../../components/Admin/ProductPagination';
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  updateInventory,
} from '../../services/productApi';

const Products = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Check if we're in form mode
  const mode = searchParams.get('mode'); // 'add', 'edit', 'view'
  const productId = searchParams.get('id');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        search: filters.search,
        category: filters.category,
        status: filters.status,
        stockStatus: filters.stockStatus || '',
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      const response = await fetchProducts(params);
      setProducts(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.total,
        totalPages: response.totalPages,
      }));
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    // Only load products if we're not in form mode
    if (!mode) {
      loadProducts();
    }
  }, [loadProducts, mode]);

  // Handle product operations
  const handleCreateProduct = async (productData) => {
    try {
      await createProduct(productData);
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleUpdateProduct = async (id, productData) => {
    try {
      await updateProduct(id, productData);
      navigate('/admin/products');
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleUpdateInventory = async (id, stockData) => {
    try {
      await updateInventory(id, stockData);
      loadProducts();
    } catch (error) {
      console.error('Error updating inventory:', error);
    }
  };

  const handleImportProducts = async (file) => {
    if (!file) return;
    setImporting(true);

    const parseCsv = (text) => {
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) return [];

      const headers = lines[0]
        .split(',')
        .map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const raw = lines[i];
        if (!raw.trim()) continue;
        const cells = raw.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = cells[idx] ?? '';
        });
        rows.push(row);
      }
      return rows;
    };

    try {
      const text = await file.text();
      const rows = parseCsv(text);

      if (!rows.length) {
        if (typeof window !== 'undefined') {
          window.alert('CSV appears to be empty or invalid.');
        }
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const row of rows) {
        try {
          const productData = {
            name: row.name,
            description: row.description,
            category: row.category,
            subcategory: row.subcategory,
            price: row.price ? Number(row.price) : 0,
            cost: row.cost ? Number(row.cost) : undefined,
            sku: row.sku,
            stock: row.stock ? Number(row.stock) : 0,
            minStock: row.minstock ? Number(row.minstock) : 0,
            status: row.status?.toLowerCase() || 'active',
            tags: row.tags
              ? row.tags
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean)
              : [],
            colors: row.colors
              ? row.colors
                  .split(',')
                  .map((c) => c.trim())
                  .filter(Boolean)
              : [],
            sizes: row.sizes
              ? row.sizes
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
            brand: row.brand,
            material: row.material,
          };

          await createProduct(productData);
          successCount += 1;
        } catch (err) {
          console.error('Error importing product row:', row, err);
          errorCount += 1;
        }
      }

      await loadProducts();

      if (typeof window !== 'undefined') {
        window.alert(
          `Import complete. Success: ${successCount}, Failed: ${errorCount}.`
        );
      }
    } catch (error) {
      console.error('Error reading CSV file:', error);
      if (typeof window !== 'undefined') {
        window.alert(
          error.message ||
            'Failed to process CSV file. Please check its format and try again.'
        );
      }
    } finally {
      setImporting(false);
    }
  };

  const handleEditProduct = (product) => {
    navigate(`/admin/products?mode=edit&id=${product.id}`);
  };

  const handleNewProduct = () => {
    navigate('/admin/products?mode=add');
  };

  const handleViewProduct = (product) => {
    navigate(`/admin/products?mode=view&id=${product.id}`);
  };

  const handleBackToList = () => {
    navigate('/admin/products');
  };

  // Render form mode (add/edit/view)
  if (mode) {
    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'add' && 'Add New Product'}
              {mode === 'edit' && 'Edit Product'}
              {mode === 'view' && 'View Product'}
            </h1>
            <p className="text-gray-600">
              {mode === 'add' && 'Create a new product in your catalog'}
              {mode === 'edit' && 'Update product information'}
              {mode === 'view' && 'View product details'}
            </p>
          </div>
          <button
            onClick={handleBackToList}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            ← Back to Products
          </button>
        </div>

        {/* Product Form */}
        <ProductForm
          product={mode === 'add' ? null : { id: productId }}
          mode={mode}
          onSubmit={
            mode === 'add'
              ? handleCreateProduct
              : mode === 'edit'
                ? (data) => handleUpdateProduct(productId, data)
                : null
          }
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Render list mode
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Products Management
          </h1>
          <p className="text-gray-600">
            Manage your product catalog and inventory
            {pagination && (
              <span className="ml-2 text-sm text-gray-500">
                ({pagination.total} products)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              !importing && document.getElementById('import-file').click()
            }
            disabled={importing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            {importing ? 'Importing...' : 'Import CSV'}
          </button>
          <button
            onClick={handleNewProduct}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <ProductFilters filters={filters} onFiltersChange={setFilters} />

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <ProductTable
          products={products}
          loading={loading}
          onEdit={handleEditProduct}
          onView={handleViewProduct}
          onDelete={handleDeleteProduct}
          onUpdateInventory={handleUpdateInventory}
        />
      </div>

      {/* Pagination */}
      <div className="mt-6">
        <ProductPagination
          pagination={pagination}
          loading={loading}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPageSizeChange={(limit) =>
            setPagination((prev) => ({ ...prev, limit, page: 1 }))
          }
        />
      </div>

      {/* Hidden file input for CSV import */}
      <input
        id="import-file"
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            handleImportProducts(file);
          }
        }}
      />
    </div>
  );
};

export default Products;
